import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import type { GsStory } from '@/lib/types';
import { formatPublished } from '@/lib/format';
import DemoBadge from '@/components/feature/DemoBadge';
import ArticleCard from './ArticleCard';

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`px-3.5 py-1.5 rounded-full text-sm font-label font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer border transition-colors ${active ? 'bg-foreground-950 text-background-50 border-foreground-950' : 'bg-background-50 text-foreground-800 border-background-300 hover:border-foreground-700'}`}>{label}</button>
  );
}

export default function NewsFeed() {
  const { data: stories, loading, error, reload } = useTable<GsStory>('gs_stories');
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(searchParams.get('category') || 'All');

  const sorted = useMemo(
    () => [...stories].sort((a, b) => (b.published_at || '').localeCompare(a.published_at || '')),
    [stories],
  );
  const lead = useMemo(() => stories.find((s) => s.is_lead) || sorted[0], [stories, sorted]);
  const secondary = useMemo(() => sorted.filter((s) => s.id !== lead?.id).slice(0, 3), [sorted, lead]);
  const categories = useMemo(() => Array.from(new Set(stories.map((s) => s.category))), [stories]);

  const rest = useMemo(() => {
    const featuredIds = new Set([lead?.id, ...secondary.map((s) => s.id)]);
    return sorted.filter((s) => !featuredIds.has(s.id) && (category === 'All' || s.category === category));
  }, [sorted, lead, secondary, category]);

  return (
    <section className="bg-background-50 px-4 md:px-8 py-14 md:py-20">
      {loading && <LoadingBlock label="Loading stories" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          {/* Featured layout */}
          {lead && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mb-16">
              <Link to={`/news/${lead.slug}`} className="group lg:col-span-7 relative block h-[380px] md:h-[560px] rounded-lg overflow-hidden cursor-pointer">
                <img src={lead.image} alt={lead.title} title={lead.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/40 to-transparent"></div>
                {lead.is_demo && <div className="absolute top-4 right-4"><DemoBadge label="Sample story" /></div>}
                <div className="absolute bottom-0 p-5 md:p-8 w-full">
                  <span className="inline-block bg-accent-500 text-foreground-950 font-label font-bold uppercase text-[11px] tracking-[0.18em] px-2.5 py-1 rounded-sm">
                    {lead.category}{lead.city ? ` \u00b7 ${lead.city}` : ''}
                  </span>
                  <h2 className="mt-4 font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-background-50 leading-[1.05] max-w-2xl">{lead.title}</h2>
                  <p className="mt-3 font-label uppercase tracking-widest text-[11px] text-background-200">
                    Featured story{lead.author ? ` \u00b7 ${lead.author}` : ''}{lead.read_time ? ` \u00b7 ${lead.read_time}` : ''}
                  </p>
                </div>
              </Link>

              <div className="lg:col-span-5 flex flex-col">
                {secondary.map((s, i) => (
                  <Link key={s.id} to={`/news/${s.slug}`} className={`group flex gap-4 md:gap-5 py-4 md:py-5 cursor-pointer items-center ${i !== 0 ? 'border-t border-background-300' : ''}`}>
                    <div className="w-24 h-20 md:w-32 md:h-24 shrink-0 overflow-hidden rounded-md">
                      <img src={s.image} alt={s.title} title={s.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-label uppercase text-[11px] tracking-[0.18em] font-semibold text-primary-600">{s.category}</p>
                      <h3 className="mt-1.5 font-heading font-bold text-lg md:text-xl leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">{s.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Category filter + grid */}
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <span className="font-label uppercase text-xs tracking-widest text-foreground-600 w-16">Category</span>
            {['All', ...categories].map((c) => <Chip key={c} label={c} active={category === c} onClick={() => setCategory(c)} />)}
          </div>

          {rest.length === 0 ? (
            <p className="py-12 text-center text-foreground-600">More stories in this category are coming soon.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((s) => <ArticleCard key={s.id} story={s} />)}
            </div>
          )}

          {category === 'All' && rest.length === 0 && (
            <p className="py-12 text-center text-foreground-600 flex items-center justify-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-information-line"></i></span>
              New stories are published regularly — check back soon.
            </p>
          )}
        </>
      )}
    </section>
  );
}